// src/contexts/SalonAuthContext.jsx - FIXED version to prevent auth conflicts
import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../utils/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getCurrentSalonUser, isSalonUserAuthenticated, logoutSalonUser } from '../api/salonAuthService';
import { 
  requestNotificationPermission, 
  saveAdminFCMToken, 
  onForegroundMessage,
  isNotificationSupported 
} from '../services/fcmService';

const SalonAuthContext = createContext();

export function useSalonAuth() {
  return useContext(SalonAuthContext);
}

export function SalonAuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isStaff, setIsStaff] = useState(false);

  // Helper function to determine if current path is salon-related
  const isSalonRelatedPath = (pathname) => {
    return pathname.includes('/admin/') || 
           pathname.includes('/BespokeBeauty/') ||
           pathname === '/login' ||
           pathname.startsWith('/admin');
  };

  // FCM token management function
  const requestAndSaveFCMToken = async (userId) => {
    try {
      if (!isNotificationSupported()) {
        console.log('Push notifications not supported in this environment');
        return;
      }

      const token = await requestNotificationPermission();
      if (token) {
        await saveAdminFCMToken(userId, token);
        console.log('FCM token saved for admin user:', userId);
      }
    } catch (error) {
      console.error('Error requesting FCM token:', error);
    }
  };

  // Foreground message listener setup
  const setupForegroundMessageListener = () => {
    return onForegroundMessage((payload) => {
      console.log('Received foreground message in admin context:', payload);
    });
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('SalonAuthContext - Auth state changed:', !!user);

      // If a user just signed in (e.g. via signInWithCustomToken), re-raise the
      // loading flag so SalonProtectedRoute keeps showing the spinner while we
      // fetch the Firestore profile. Without this, loading stays false from the
      // initial null-user fire and the route redirects before currentUser is set.
      if (user) {
        setLoading(true);
      }

      try {
        if (user) {
          console.log('SalonAuthContext - User exists, checking if salon staff...');
          
          // Check if user is salon staff
          const isSalon = await isSalonUserAuthenticated();
          console.log('SalonAuthContext - Is salon staff:', isSalon);
          
          if (isSalon) {
            const salonUser = await getCurrentSalonUser();
            console.log('SalonAuthContext - Salon user data:', salonUser);
            
            setCurrentUser(salonUser);
            setIsStaff(true);
            
            // Request FCM token for salon admin users (non-blocking)
            if (salonUser?.uid) {
              requestAndSaveFCMToken(salonUser.uid);
              setupForegroundMessageListener();
            }
            
          } else {
            // FIXED: Only clear salon state if on salon/admin paths to prevent conflicts
            const currentPath = window.location.pathname;
            const isSalonPath = isSalonRelatedPath(currentPath);
            
            console.log('SalonAuthContext - Not salon staff, current path:', currentPath, 'is salon path:', isSalonPath);
            
            if (isSalonPath) {
              // Not a salon staff member but on salon path
              setCurrentUser(null);
              setIsStaff(false);
            }
            // CRITICAL: If not on salon path, don't change state to prevent conflicts with other auth contexts
          }
        } else {
          console.log('SalonAuthContext - No user, checking if should clear state...');
          
          // FIXED: Only clear state if on salon paths or during explicit logout
          const currentPath = window.location.pathname;
          const isSalonPath = isSalonRelatedPath(currentPath);
          const isExplicitLogout = sessionStorage.getItem('salon_explicit_logout') === 'true';
          
          console.log('SalonAuthContext - Clear conditions:', {
            currentPath,
            isSalonPath,
            isExplicitLogout
          });
          
          if (isSalonPath || isExplicitLogout) {
            setCurrentUser(null);
            setIsStaff(false);
            // Clear the explicit logout flag
            sessionStorage.removeItem('salon_explicit_logout');
          }
          // CRITICAL: Don't clear state if not on salon path to prevent interfering with other auth contexts
        }
      } catch (error) {
        console.error('SalonAuthContext - Error in auth state change:', error);
        
        // FIXED: Only clear on error if on salon paths
        const currentPath = window.location.pathname;
        const isSalonPath = isSalonRelatedPath(currentPath);
        
        if (isSalonPath) {
          setCurrentUser(null);
          setIsStaff(false);
        }
      } finally {
        // Always set loading to false at the end
        console.log('SalonAuthContext - Setting loading to false');
        setLoading(false);
      }
    });
    
    return unsubscribe;
  }, []);

  // Enhanced logout handler for Salon
  const handleSalonLogout = async () => {
    try {
      console.log('SalonAuthContext - Handling salon logout...');
      
      // Set flag to indicate this is an explicit Salon logout
      sessionStorage.setItem('salon_explicit_logout', 'true');
      
      // Clear local state immediately
      setCurrentUser(null);
      setIsStaff(false);
      
      // Use the logout function
      await logoutSalonUser();
      
      console.log('SalonAuthContext - Salon logout successful');
      return true;
    } catch (error) {
      console.error('SalonAuthContext - Error in Salon logout:', error);
      sessionStorage.removeItem('salon_explicit_logout');
      throw error;
    }
  };

  const value = {
    currentUser,
    isStaff,
    loading,
    logout: handleSalonLogout
  };

  console.log('SalonAuthContext - Providing context values:', {
    hasCurrentUser: !!currentUser,
    isStaff,
    loading,
    userRole: currentUser?.role,
    userSalonId: currentUser?.salonId
  });
  
  return (
    <SalonAuthContext.Provider value={value}>
      {children}
    </SalonAuthContext.Provider>
  );
}